import React, { useMemo, useState, useEffect, useRef, useCallback, useContext } from "react";
import { Box, Typography, Button, CircularProgress, Divider, Grid, Paper, Stack, Avatar, Chip, Tooltip } from "@mui/material";
import { green } from "@mui/material/colors";
import CallIcon from "@mui/icons-material/Call";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { getReadyOrders, initiateExotelCall } from "../../../../service/order/orderService";
import { useParams } from "react-router-dom";
import vegIcon from "../../../../Assets/Veg_symbol.svg.png"
import nonVegIcon from "../../../../Assets/Non_veg_symbol.svg.png"
import eggIcon from "../../../../Assets/Egg.svg"
import { OrderCountsContext } from "../../Tabs/InvoiceTabs";
import { useAppSelector } from "../../../../Redux/hooks";
import { notifyError, notifySuccess } from "../../../../components/ToastContainer/Toast/Toast";
import CallInitiatedModal from "../../../../components/CallInitiatedModal/CallInitiatedModal";
import CopyToClipboardIcon from "../../../../components/Copy/CopyToClipboardIcon";
import {
  OrderPickupDeliveryCodes,
  resolvePickupDeliveryCodes,
} from "../OrderPickupDeliveryCodes";

// Food type icon component
const FoodTypeIcon = ({ productType }: { productType: string }) => {
    switch (productType) {
        case 'veg':
            return <img src={vegIcon} alt="Veg" style={{ width: 16, height: 16, marginRight: 8 }} />;
        case 'non-veg':
            return <img src={nonVegIcon} alt="Non-Veg" style={{ width: 16, height: 16, marginRight: 8 }} />;
        case 'egg':
            return <img src={eggIcon} alt="Egg" style={{ width: 16, height: 16, marginRight: 8 }} />;
        default:
            return null;
    }
};
const isTazty = String(process.env.REACT_APP_ORG_CODE ?? "").trim().toLowerCase() === "tazty";

// Helper function to calculate time ago string (re-used from New component)
const getTimeAgo = (dateString: string | Date): string => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    let interval = seconds / 31536000;
    if (interval > 1) {
        return Math.floor(interval) + " years ago";
    }
    interval = seconds / 2592000;
    if (interval > 1) {
        return Math.floor(interval) + " months ago";
    }
    interval = seconds / 86400;
    if (interval > 1) {
        return Math.floor(interval) + " days ago";
    }
    interval = seconds / 3600;
    if (interval > 1) {
        return Math.floor(interval) + " hours ago";
    }
    interval = seconds / 60;
    if (interval > 1) {
        return Math.floor(interval) + " minutes ago";
    }
    return Math.floor(seconds) + " seconds ago";
};

// Interface for order data, assuming the structure received from API
interface OrderData {
    instructions: string;
    id: string;
    order_id: string;
    order_code: string;
    order_date?: string; // Or updated_at / invoice_date
    invoice_date?: string;
    updated_at?: string; // Add if available and relevant for placing time
    customer?: {
        name: string;
        phone: string;
        order_count?: number;
        billing_address: {
            // address1: string;
            // address2?: string;
            address3?: string;
            city: string;
            state: string;
            pincode: string;
        };
    };
    customer_name?: string;
    customer_phone?: string;
    billing_address?: any; // invoices may have a string/object
    total_amount?: string | number;
    amount?: string | number;
    total?: string | number;
    payment_method?: string;
    payment_status?: string;
    instruction?: string;
    cutlery_instruction?: string; // Assuming this field exists
    order_details?: Array<{
        product_id: string;
        product_name: string;
        quantity: number;
        price: string;
        is_veg?: boolean;
        addons?: Array<{ name: string }>;
    }>;
    order_items?: Array<any>;
    items?: Array<any>;
    igst: number;
    sgst: number;
    cgst: number;
    shipping_charge: number;
    invoice_items?: Array<any>;
    agent_details?: {
        id?: string;
        name?: string;
        phone?: string;
        phone_number?: string;
        image?: string;
        status_history?: Array<{
            status: string;
            timestamp: string;
        }>;
    } | null;
    delivery_time_estimate?: string;
    status: any; // use raw invoice status for filtering/rendering
    sub_total?: string | number;
    sybTotalAmount?: string;
}

// Sub-component for a single ready order box
interface SingleReadyOrderBoxProps {
    order: OrderData;
    fetchInvoiceData: () => void;
}

const SingleReadyOrderBox: React.FC<SingleReadyOrderBoxProps> = ({ order, fetchInvoiceData }) => {
    const [timeAgoString, setTimeAgoString] = useState("N/A");
    const [calling, setCalling] = useState(false);
    const [callingAgent, setCallingAgent] = useState(false);
    const [showCallInitiatedModal, setShowCallInitiatedModal] = useState(false);
    const [showAgentCallInitiatedModal, setShowAgentCallInitiatedModal] = useState(false);
    
    // Get profile phone_number from Redux
    const profilePhoneNumber = useAppSelector((state) => state.roleAndPermission?.phone_number || '');

    const currentStatus = order.status?.toLowerCase() || 'packed';
    const { pickupCode, deliveryCode } = useMemo(
      () => resolvePickupDeliveryCodes(order as any),
      [order],
    );

    const currentOrder = useMemo(() => {
        const dateStr = order.updated_at || order.invoice_date || order.order_date || '';
        const orderDateTime = new Date(dateStr);
        const hasValidDate = !isNaN(orderDateTime.getTime());

        const customerName = order.customer_name || order.customer?.name || 'N/A';
        const customerPhone = order.customer_phone || order.customer?.phone || 'N/A';

        const billing = order.billing_address || order.customer?.billing_address || {};
        const address1 = typeof billing === 'string' ? '' : billing?.address1;
        const address2 = typeof billing === 'string' ? '' : billing?.address2;
        const address3 = typeof billing === 'string' ? '' : billing?.address3;
        const city = typeof billing === 'string' ? '' : billing?.city;
        const state = typeof billing === 'string' ? '' : billing?.state;
        const pincode = typeof billing === 'string' ? '' : billing?.pincode;

        const items = order.items || [];

        const shippingCharge = order.shipping_charge ? parseFloat(String(order.shipping_charge)).toFixed(2) : undefined;
        const cgst = order.cgst ? parseFloat(String(order.cgst)).toFixed(2) : undefined;
        const sgst = order.sgst ? parseFloat(String(order.sgst)).toFixed(2) : undefined;
        const igst = order.igst ? parseFloat(String(order.igst)).toFixed(2) : undefined;

        const totalAmountRaw = order.total_amount ?? order.amount ?? order.total ?? 0;
        const totalAmount = parseFloat(String(totalAmountRaw)).toFixed(2);
        // const paymentStatus = order.payment_method === 'online' || order.payment_status === 'paid' ? 'Paid' : 'Not Paid';
        const paymentStatus = String(order.payment_status).toLowerCase() === 'received' ? 'Paid' : 'Not Paid';
        const agentDetails = order.agent_details || null;

        return {
            id: order.id,
            orderId: order.order_id,
            order_code: order.order_code,
            time: hasValidDate ? orderDateTime.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' }) : 'N/A',
            fullOrderDate: hasValidDate ? orderDateTime : undefined,
            customerName,
            customerPhone,
            addressLines: [
                address1, 
                address2, 
                address3
            ].filter(Boolean).join(', '),
            cityStatePincode: [city, state, pincode].filter(Boolean).join(', '),
            totalAmount,
            paymentStatus,
            instruction: order?.instructions || order.instruction || 'No special instructions',
            cutleryInstruction: order.cutlery_instruction || null,
            items, // Keep original items for detailed grouping logic
            shippingCharge,
            cgst,
            sgst,
            igst,
            agentDetails: agentDetails ? {
                id: agentDetails.id || 'N/A',
                name: agentDetails.name || 'Delivery Partner',
                phone: agentDetails.phone || agentDetails.phone_number || 'N/A',
                image: agentDetails.image || null,
                status_history: agentDetails.status_history || []
            } : null,
            customerOrderCount: order.customer?.order_count ? `${order.customer.order_count}th Order` : 'N/A',
            deliveryTimeEstimate: order.delivery_time_estimate || 'N/A',
            orderStatus: order.status,
            sybTotalAmount: order.sub_total != null ? parseFloat(String(order.sub_total)).toFixed(2) : undefined
        };
    }, [order]);

    const groupedDisplayItems = useMemo(() => {
        const baseProductsMap = new Map<string, any>();

        currentOrder.items.forEach((item: any) => {
            // Check if it's an add-on based on product_id starting with 'a-'
            if (String(item.product_id).startsWith("a-")) {
                // Attempt to extract the base product ID from the add-on ID (e.g., 'a-21783-735' -> '21783')
                // This assumes the pattern 'a-{baseProductId}-{someOtherId}'
                const match = String(item.product_id).match(/a-(\d+)(?:-\d+)?/);
                const baseProductId = match ? match[1] : null;

                if (baseProductId && baseProductsMap.has(baseProductId)) {
                    // If the base product exists in our map, add this item as an addon to it
                    const baseItem = baseProductsMap.get(baseProductId);
                    if (!baseItem.addons) {
                        baseItem.addons = [];
                    }
                    baseItem.addons.push(item);
                } else {
                    // If no corresponding base product is found or it's an unrecognized format,
                    // treat it as a standalone item for display
                    if (!baseProductsMap.has(item.product_id)) {
                        baseProductsMap.set(item.product_id, { ...item, addons: [] });
                    }
                }
            } else {
                // It's a base product
                if (!baseProductsMap.has(item.product_id)) {
                    baseProductsMap.set(item.product_id, { ...item, addons: [] });
                }
            }
        });

        return Array.from(baseProductsMap.values());
    }, [currentOrder.items]);

    useEffect(() => {
        if (currentOrder.fullOrderDate) {
            const updateTimeAgo = () => {
                setTimeAgoString(getTimeAgo(currentOrder.fullOrderDate));
            };

            updateTimeAgo();
            const intervalId = setInterval(updateTimeAgo, 60 * 1000); // Update every minute

            return () => clearInterval(intervalId);
        } else {
            setTimeAgoString("N/A");
        }
    }, [currentOrder.fullOrderDate]);

    const formatStatus = (status: string) =>
        status
            .replace(/-/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase());

    // Format status for history display: replace underscores with spaces and capitalize first letter
    const formatStatusHistory = (status: string) => {
        if (!status) return '';
        // Replace underscores with spaces
        const withSpaces = status.replace(/_/g, ' ');
        // Capitalize first letter only
        return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1).toLowerCase();
    };

    const getStatusChipStyle = (status: string) => {
        const statusLower = status?.toLowerCase();
        switch (statusLower) {
            case 'packed':
                return {
                    bgcolor: "#E3F2FD",
                    color: "#1976D2",
                    fontWeight: 600,
                    px: 1,
                };
            case 'agent-assigned':
                return {
                    bgcolor: "#E1F5FE",
                    color: "#0277BD",
                    fontWeight: 600,
                    px: 1,
                };
            case 'agent-arrived-restaurant':
                return {
                    bgcolor: "#E8EAF6",
                    color: "#3F51B5",
                    fontWeight: 600,
                    px: 1,
                };
            case 'picked':
                return {
                    bgcolor: "#FFF3E0",
                    color: "#F57C00",
                    fontWeight: 600,
                    px: 1,
                };
            case 'out-for-delivery':
                return {
                    bgcolor: "#F3E5F5",
                    color: "#7B1FA2",
                    fontWeight: 600,
                    px: 1,
                };
            case 'delivered':
                return {
                    bgcolor: "#E8F5E9",
                    color: "#2E7D32",
                    fontWeight: 600,
                    px: 1,
                };
            default:
                return {
                    bgcolor: "#F5F5F5",
                    color: "#616161",
                    fontWeight: 600,
                    px: 1,
                };
        }
    };

    const phone = currentOrder?.customerPhone;

    const handleCallClick = async () => {
        if (!phone || phone === "N/A" || !profilePhoneNumber) {
            notifyError("Phone number not available");
            return;
        }

        if (!currentOrder.order_code) {
            notifyError("Order code not available");
            return;
        }

        setCalling(true);
        try {
            const payload = {
                order_id: currentOrder.order_code,
                from: profilePhoneNumber,
                to: phone,
            };
            const response = await initiateExotelCall(payload);
            if (response?.status === 200 || response?.status === 201) {
                setShowCallInitiatedModal(true);
            } else {
                const errorMessage = response?.data?.message || "Failed to initiate call";
                notifyError(errorMessage === "Internal Server Error" ? "Something went wrong!" : errorMessage);
            }
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || "Failed to initiate call";
            notifyError(errorMessage === "Internal Server Error" ? "Something went wrong!" : errorMessage);
        } finally {
            setCalling(false);
        }
    };

    return (
        <>
        <Paper
            sx={{
                display: "flex",
                borderRadius: 1,
                border: "1px solid #ddd",
                overflow: "hidden",
                mb: 2,
            }}
        >
            <Box
                sx={{
                    bgcolor: "#f9f9f9",
                    flex: "0 0 35%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    borderRight: "1px dashed #ccc",
                    p: 2,
                    gap: 1.5,
                }}
            >
                <Box
                    sx={{
                        bgcolor: "#f9f9f9",
                        flex: "0 0 35%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        gap: 1.5,
                    }}
                >
                    <Box>
                        <Box sx={{ mb: 0.5 }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 0.25 }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    Order ID
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                                    {currentOrder.time}
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <Box sx={{ bgcolor: '#E0F2F1', px: 1.5, py: 0.5, borderRadius: '6px', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                                    <Typography fontWeight={600} fontSize={12}>
                                        {currentOrder.order_code}
                                    </Typography>
                                </Box>
                                <CopyToClipboardIcon value={currentOrder.order_code || ""} title="Copy Order ID" size={16} />
                            </Box>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: 10, textTransform: 'uppercase', mb: 0.25, display: 'block', letterSpacing: 0.5 }}>
                                Invoice ID
                            </Typography>
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <Box sx={{ bgcolor: '#E0F2F1', px: 1.5, py: 0.5, borderRadius: '6px', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                                    <Typography fontWeight={600} fontSize={12}>
                                        {currentOrder.id}
                                    </Typography>
                                </Box>
                                <CopyToClipboardIcon value={String(currentOrder.id)} title="Copy Invoice ID" size={16} />
                            </Box>
                        </Box>
                    </Box>
                    {isTazty && (
                        <OrderPickupDeliveryCodes
                          tab="ready"
                          pickupCode={pickupCode}
                          deliveryCode={deliveryCode}
                        />
                    )}

                    <Divider />

                    <Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box>
                                <Typography variant="body2" fontWeight="bold" sx={{ pt: 0.5, pb: 0.5 }}>
                                    {currentOrder.customerName}
                                </Typography>
                                {/* <Typography fontSize={12} fontWeight={500} sx={{ pt: 0.5 }}>
                                {currentOrder.customerOrderCount}
                            </Typography> */}
                            </Box>

                            <Box
                                display="flex"
                                alignItems="center"
                                sx={{
                                    color: green[600],
                                    cursor: phone !== "N/A" && profilePhoneNumber ? "pointer" : "default",
                                    overflow: "hidden",
                                    width: "fit-content",
                                }}
                                onClick={handleCallClick}
                            >
                                <CallIcon 
                                    fontSize="small" 
                                    sx={{ 
                                        mr: 0.5, 
                                        transition: "transform 0.3s",
                                        opacity: calling ? 0.6 : 1,
                                        cursor: phone !== "N/A" && profilePhoneNumber ? "pointer" : "default"
                                    }} 
                                />
                            </Box>
                        </Box>

                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mt={0.5}
                        >
                            <Box sx={{ maxWidth: '70%' }}>
                                <Typography
                                    fontSize={12}
                                    fontWeight={500}
                                    sx={{ display: 'block' }}
                                >
                                    {currentOrder.addressLines}
                                </Typography>
                                <Typography
                                    fontSize={12}
                                    fontWeight={500}
                                    color="text.secondary"
                                    sx={{ display: 'block' }}
                                >
                                    {currentOrder.cityStatePincode}
                                </Typography>
                            </Box>
                            {/* <Typography fontSize={12} color="grey" sx={{ whiteSpace: 'nowrap' }}>
                                {currentOrder.deliveryTimeEstimate}
                            </Typography> */}
                        </Box>
                    </Box>

                    <Divider />

                    <Box>
                        <Typography fontSize={12} fontWeight={500} color="grey">
                            Instruction:
                        </Typography>
                        <Typography fontSize={12} fontWeight={500}>
                            {currentOrder.instruction}
                        </Typography>
                    </Box>

                    <Typography
                        variant="caption"
                        fontWeight={500}
                        display={"flex"}
                        justifyContent={"flex-end"}
                    >
                        Placed {timeAgoString}
                    </Typography>
                </Box>
            </Box>

            <Box
                sx={{
                    flex: "0 0 68%",
                    display: "flex",
                    flexDirection: "row",
                    backgroundColor: "white",
                }}
            >
                {/* Items section */}
                <Box
                    sx={{
                        flex: "0 0 60.5%",
                        p: 2,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-start",
                        borderRight: "1px dashed #ccc",
                        gap: 2,
                    }}
                >
                    {/* Don't send cutlery chip */}
                    {currentOrder.cutleryInstruction && (
                        <Chip
                            label={currentOrder.cutleryInstruction}
                            sx={{
                                borderColor: "#d4af37",
                                color: "#d4af37",
                                backgroundColor: "transparent",
                                border: "1px solid",
                                borderRadius: "20px",
                                fontSize: "0.75rem",
                                fontWeight: 500,
                                alignSelf: "flex-start",
                                height: 24,
                            }}
                        />
                    )}

                    {/* Items list */}
                    <Stack spacing={1}>
                        {groupedDisplayItems.map((item: any, i: number) => (
                            <>
                                <Box key={item.product_id || i} // Use product_id for key, fallback to index
                                    display="flex"
                                    flexDirection="column" // Changed to column for stacking name+qty and addons
                                    alignItems="flex-start"
                                    mb={1}>
                                    <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <FoodTypeIcon productType={item.product_type} />
                                            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                                                {item.product_name} x {item.quantity}
                                            </Typography>
                                        </Box>
                                        <Typography fontWeight={500}>₹{parseFloat(item.total_price).toFixed(2)}</Typography>
                                    </Box>
                                    {item.addons && item.addons.length > 0 && (
                                        <Box mt={0.5} width="100%">
                                            {/* <Typography variant="caption" fontWeight={600} color="text.secondary">
                                                        Add ons :
                                                    </Typography> */}
                                            {item.addons.map((addon: any, idx: number) => (
                                                <Box key={idx} display="flex" justifyContent="space-between" width="100%" sx={{ pl: 2 }}>
                                                    <Typography key={idx} variant="caption" color="text.secondary" display="block" sx={{ pl: 1 }}>
                                                        • {addon.product_name} x {addon.quantity}
                                                    </Typography>
                                                    <Typography key={idx} variant="caption" color="text.secondary" display="block" sx={{ pl: 1 }}>
                                                        ₹{Number(addon.total_price).toFixed(2)}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            </>
                        ))}
                    </Stack>

                    <Divider />

                    <Box display="flex" flexDirection="column" alignItems="flex-start" mb={1}>
                        <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">    
                            <Typography variant="body2" fontSize={12} fontWeight={600} color="grey">  Sub Total: </Typography>
                            <Typography variant="body2" fontSize={12} fontWeight={600} color="grey">
                                ₹{currentOrder.sybTotalAmount}
                            </Typography>
                        </Box>
                    </Box>

                    <Divider />

                    <Box
                        sx={{
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            p: 2,
                            // mt: 2,
                        }}
                    >
                        <Grid container>
                            {/* Left side labels */}
                            <Grid item xs={9}>
                                {/* <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                    Delivery Charge
                                </Typography> */}

                                {Number(currentOrder?.cgst) !== 0 ? (
                                    <>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                            CGST
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                            SGST
                                        </Typography>
                                    </>
                                ) : Number(currentOrder?.igst) !== 0 ? (
                                    <>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                            IGST
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                            SGST
                                        </Typography>
                                    </>
                                ) : null}
                            </Grid>

                            {/* Right side values */}
                            <Grid item xs={3} textAlign="right">
                                {/* <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                    ₹{Number(currentOrder?.shippingCharge || 0).toFixed(2)}
                                </Typography> */}

                                {Number(currentOrder?.cgst) !== 0 ? (
                                    <>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                            ₹{Number(currentOrder?.cgst || 0).toFixed(2)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                            ₹{Number(currentOrder?.sgst || 0).toFixed(2)}
                                        </Typography>
                                    </>
                                ) : Number(currentOrder?.igst) !== 0 ? (
                                    <>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                            ₹{Number(currentOrder?.igst || 0).toFixed(2)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                            ₹{Number(currentOrder?.sgst || 0).toFixed(2)}
                                        </Typography>
                                    </>
                                ) : null}
                            </Grid>
                        </Grid>

                        {/* Divider */}
                        <Box sx={{ borderTop: "1px dashed #e0e0e0", my: 1 }} />

                        {/* Total row */}
                        <Grid container>
                            <Grid item xs={9} display="flex" alignItems="center">
                                <Typography fontWeight="bold" mr={1}>
                                    Total
                                </Typography>
                                <Box
                                    sx={{
                                        bgcolor: currentOrder.paymentStatus === "Paid" ? "green" : "orange",
                                        color: "white",
                                        fontSize: "0.7rem",
                                        px: 1,
                                        py: 0.2,
                                        borderRadius: "6px",
                                        display: "inline-flex",
                                        alignItems: "center",
                                    }}
                                >
                                    {currentOrder.paymentStatus}
                                </Box>
                            </Grid>
                            <Grid item xs={3} textAlign="right">
                                <Typography fontWeight={600}>₹{currentOrder.totalAmount}</Typography>
                            </Grid>
                        </Grid>
                    </Box>
                </Box>

                {/* Delivery partner details */}
                <Box
                    sx={{
                        flex: "0 0 35%",
                        p: 2,
                        backgroundColor: "#f9f9f9",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    {currentOrder?.agentDetails ? (
                        <Paper
                            elevation={0}
                            sx={{
                                width: "100%",
                                height: 'auto',
                                borderRadius: 1,
                                border: "1px solid #ddd",
                                overflow: "hidden",
                            }}
                        >
                            {/* Common Header */}
                            <Box
                                sx={{
                                    bgcolor: "#f9f9f9",
                                    p: 1.5,
                                }}
                            >
                                <Typography fontWeight="bold">Order was prepared on time</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Timely delivery to the customer
                                </Typography>
                            </Box>

                            {/* Content */}
                            <Box sx={{ bgcolor: "white", p: 2 }}>
                                <Box display="flex" alignItems="center" gap={1.5}>
                                    <Avatar 
                                        src={currentOrder.agentDetails.image || undefined} 
                                        sx={{ borderRadius: 1 }}
                                    >
                                        {currentOrder.agentDetails.image 
                                            ? null 
                                            : currentOrder.agentDetails.name 
                                                ? currentOrder.agentDetails.name.charAt(0).toUpperCase()
                                                : "D"
                                        }
                                    </Avatar>

                                    <Box>
                                        <Typography fontWeight="bold" pb={.5}>
                                            {currentOrder.agentDetails.name}
                                        </Typography>
                                        <Chip
                                            label={formatStatus(currentStatus)}
                                            sx={getStatusChipStyle(currentStatus)}
                                        />
                                    </Box>
                                </Box>

                                {/* Status History */}
                                {currentOrder.agentDetails.status_history && currentOrder.agentDetails.status_history.length > 0 && (
                                    <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #e0e0e0" }}>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                            <Typography variant="caption" fontWeight={600} color="text.secondary">
                                                Status History
                                            </Typography>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<CallIcon fontSize="small" sx={{ color: green[600] }} />}
                                                disabled={callingAgent || !currentOrder.agentDetails?.phone || currentOrder.agentDetails.phone === 'N/A' || !profilePhoneNumber || !currentOrder.order_code}
                                                sx={{
                                                    textTransform: "none",
                                                    borderRadius: 1,
                                                    fontSize: 12,
                                                    color: green[600],
                                                    borderColor: 'transparent',
                                                    backgroundColor: 'transparent',
                                                    "&:hover": { borderColor: green[600], color: green[600], backgroundColor: 'transparent' },
                                                    "&:disabled": { opacity: 0.5 },
                                                    px: 1.5,
                                                    py: 0.5,
                                                }}
                                                onClick={async () => {
                                                    const agentPhone = currentOrder.agentDetails?.phone;
                                                    if (!agentPhone || agentPhone === "N/A" || !profilePhoneNumber || !currentOrder.order_code) {
                                                        notifyError("Phone number or order code not available");
                                                        return;
                                                    }

                                                    setCallingAgent(true);
                                                    try {
                                                        const payload = {
                                                            order_id: currentOrder.order_code,
                                                            from: profilePhoneNumber,
                                                            to: agentPhone,
                                                        };
                                                        const response = await initiateExotelCall(payload);
                                                        if (response?.status === 200 || response?.status === 201) {
                                                            setShowAgentCallInitiatedModal(true);
                                                        } else {
                                                            const errorMessage = response?.data?.message || "Failed to initiate call";
                                                            notifyError(errorMessage === "Internal Server Error" ? "Something went wrong!" : errorMessage);
                                                        }
                                                    } catch (error: any) {
                                                        const errorMessage = error?.response?.data?.message || "Failed to initiate call";
                                                        notifyError(errorMessage === "Internal Server Error" ? "Something went wrong!" : errorMessage);
                                                    } finally {
                                                        setCallingAgent(false);
                                                    }
                                                }}
                                            >
                                                Call
                                            </Button>
                                        </Box>
                                        {/* <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                            {currentOrder.agentDetails.status_history.map((item: any, idx: number) => (
                                                <Box 
                                                    key={idx}
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        p: 1,
                                                        bgcolor: "#f9f9f9",
                                                        borderRadius: 1,
                                                        border: "1px solid #e0e0e0",
                                                    }}
                                                >
                                                    <Typography variant="caption" fontWeight={500}>
                                                        {formatStatusHistory(item.status || item)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {item.timestamp ? new Date(item.timestamp).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true }) + ' | ' + new Date(item.timestamp).toISOString().split('T')[0] : 'N/A'}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box> */}
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    ) : (
                        <Box
                            sx={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                p: 2,
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "text.secondary",
                                    fontSize: 12,
                                    fontWeight: 500,
                                }}
                            >
                                No Delivery Partner Available
                            </Typography>
                        </Box>
                    )}
                </Box > 
            </Box >
        </Paper >
        <CallInitiatedModal
            open={showCallInitiatedModal}
            onClose={() => setShowCallInitiatedModal(false)}
            phoneNumber={profilePhoneNumber}
            recipientType="customer"
        />
        <CallInitiatedModal
            open={showAgentCallInitiatedModal}
            onClose={() => setShowAgentCallInitiatedModal(false)}
            phoneNumber={profilePhoneNumber}
            recipientType="agent"
        />
        </>
    );
};

const Ready: React.FC = () => {
    const [invoiceData, setInvoiceData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [fetchingMore, setFetchingMore] = useState(false);
    const { store_id } = useParams();

    
    // Get the context for sharing counts and auto-refresh state
    const orderCountsContext = useContext(OrderCountsContext);
    const { updateCount, countsLoaded, refreshCallbacksRef } = orderCountsContext || { 
      updateCount: () => {}, 
      countsLoaded: false,
      refreshCallbacksRef: { current: {} as { preparing?: () => void; ready?: () => void } }
    };

    const fetchInvoices = useCallback(async (pageToFetch = 1, append = false, showLoading = true) => {
        if (pageToFetch > totalPages) return;
        if (pageToFetch === 1 && showLoading) setLoading(true);
        else if (pageToFetch > 1) setFetchingMore(true);
        setError(null);
        try {
            const res = await getReadyOrders(pageToFetch, store_id as any, undefined, showLoading);
            const data = res?.data?.data;
            const invoices = data?.invoice || data?.orders || data || [];
            setTotalPages(data?.totalPages || 1);
            const newTotalCount = data?.total || invoices.length;
            setTotalCount(newTotalCount);
            // Update the context with the new count (only if counts haven't been loaded yet)
            if (!countsLoaded) {
                updateCount('ready', newTotalCount);
            }
            if (append) {
                setInvoiceData(prev => [...prev, ...(Array.isArray(invoices) ? invoices : [])]);
            } else {
                setInvoiceData(Array.isArray(invoices) ? invoices : []);
            }
        } catch (e: any) {
            setError(e?.message || "Failed to fetch invoices");
            if (!append) setInvoiceData([]);
        } finally {
            if (pageToFetch === 1 && showLoading) setLoading(false);
            else if (pageToFetch > 1) setFetchingMore(false);
        }
    }, [store_id, totalPages, countsLoaded, updateCount]);

    useEffect(() => {
        setPage(1);
        fetchInvoices(1, false);
    }, [store_id]);

    // Register refresh callback with parent component
    useEffect(() => {
        if (refreshCallbacksRef && refreshCallbacksRef.current) {
            refreshCallbacksRef.current.ready = () => {
                fetchInvoices(1, false, false); // showLoading = false
            };
        }
        return () => {
            if (refreshCallbacksRef && refreshCallbacksRef.current) {
                delete refreshCallbacksRef.current.ready;
            }
        };
    }, [fetchInvoices, refreshCallbacksRef]);



    // Show orders with 'packed', 'picked', 'out-for-delivery', 'agent-assigned', and 'agent-arrived-restaurant' statuses
    const readyStatuses = ["packed", "picked", "out-for-delivery", "agent-assigned", "agent-arrived-restaurant"];
    const filtered = Array.isArray(invoiceData)
        ? invoiceData
            .filter((inv: any) => readyStatuses.includes(inv?.status?.toLowerCase?.()))
            .map((inv: any) => ({
                ...inv,
                items: inv.items?.filter((item: any) => readyStatuses.includes(inv?.status?.toLowerCase?.())),
            }))
            .filter((inv: any) => inv.items && inv.items.length > 0)
        : [];

    return (
        <Box>

            {loading ? (
                <Typography>Loading...</Typography>
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : filtered.length > 0 ? (
                <>
                    {filtered.map((order, index) => (
                        <SingleReadyOrderBox
                            key={order.id || index}
                            order={order as OrderData}
                            fetchInvoiceData={fetchInvoices}
                        />
                    ))}
                    {page < totalPages && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={fetchingMore ? <CircularProgress size={16} /> : <AddCircleOutlineIcon />}
                                disabled={fetchingMore}
                                onClick={() => {
                                    const nextPage = page + 1;
                                    setPage(nextPage);
                                    fetchInvoices(nextPage, true, false);
                                }}
                                sx={{ textTransform: 'none' }}
                            >
                                {fetchingMore ? 'Loading...' : 'View More'}
                            </Button>
                        </Box>
                    )}
                </>
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 'auto',
                        p: 2,
                    }}
                >
                    <img
                        src=""
                        alt=""
                        style={{
                            maxWidth: '100%',
                            height: 'auto',
                            display: 'block',
                            marginBottom: '16px',
                        }}
                    />
                    <Typography variant="body1" color="text.secondary">
                        No orders in the Ready stage yet!
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default Ready;
